import colors from '@honeybook/hbui/dist/styles/colors';
import Web24 from '@honeybook/icons/dist/icons/Web24';
import HBText from '@honeybook/hbui/dist/components/HBText/HBText';
import HBButton from '@honeybook/hbui/dist/components/HBButton/HBButton';
import Open24 from '@honeybook/icons/dist/icons/Open24';
import Link16 from '@honeybook/icons/dist/icons/Link16';
import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import HBBanner from '@honeybook/hbui/dist/components/HBBanner/HBBanner';
import { useTranslation } from 'react-i18next';
import { NAMESPACES } from '~/i18n/namespaces';
import { PublicTemplateConfigurationProps } from '~/components/Templates/components/FlowTemplates/PublicTemplateConfigurationModal';
import { observer } from 'mobx-react';
import HBModal from '@honeybook/hbui/dist/components/HBModal/HBModal';
import TriangleExclamationMark42 from '@honeybook/icons/dist/icons/TriangleExclamationMark42';
import HBTooltip from '@honeybook/hbui/dist/components/HBTooltip/HBTooltip';
import { mediumUp, upToMedium } from '@honeybook/hbui/dist/styles/breakpoints';
import { useWindowSize } from '@honeybook/hbui/dist/components/common/useWindowSize';
import { usePublishedTemplate } from '~/components/Templates/components/FlowTemplates/usePublishedTemplate';
import HBLink from '@honeybook/hbui/dist/components/HBLink/HBLink';
import Copy16 from '@honeybook/icons/dist/icons/Copy16';
import { useBuildTestId } from '@honeybook/hbui/dist/components/common/useBuildTestId';
import is from 'styled-is';

const PublishedTemplateModalContent = ({
    publicConfiguration,
    noTabs
}: PublicTemplateConfigurationProps & { noTabs: boolean }) => {
    const { t } = useTranslation(NAMESPACES.TEMPLATES, {
        keyPrefix: 'share_template.publish_template'
    });
    const [isDeactivateModalOpen, setIsDeactivateModalOpen] = useState(false);
    const { isMediumUp } = useWindowSize();
    const { deactivateLink, copyLink, isLoading, trackEvent } =
        usePublishedTemplate({ publicConfiguration });

    const fontSize = isMediumUp ? 'body14' : 'body16';

    const openLinkButton = useMemo(() => {
        const button = (
            <LinkButton
                id=""
                onClick={() => {
                    trackEvent({ event: 'open share link' });
                    window.open(publicConfiguration?.public_url, '_blank');
                }}
                buttonTheme="secondary-solid-secondary"
                customTheme={{
                    background: colors.neutral100,
                    color: colors.black,
                    fontSize: fontSize
                }}
                size="sm"
            >
                <Open24 color={colors.black} />
                {!isMediumUp && t('open_link_cta')}
            </LinkButton>
        );
        return !isMediumUp ? (
            button
        ) : (
            <HBTooltip target={button}>{t('open_link_tooltip')}</HBTooltip>
        );
    }, [fontSize, isMediumUp, publicConfiguration?.public_url, t, trackEvent]);

    const copyButtonIcon = useMemo(() => {
        return isMediumUp ? (
            <Link16 color={colors.white} />
        ) : (
            <Copy16 color={colors.white} />
        );
    }, [isMediumUp]);

    const templateLinkTestId = useBuildTestId({
        componentName: 'PublishedTemplateModalContent',
        identifier: 'template',
        suffix: 'link'
    });

    return (
        <ModalContent noTabs={noTabs}>
            <Banner
                noTabs={noTabs}
                customTheme={{
                    barColor: colors.green50,
                    backgroundColor: '#6EE59A14',
                    fontSize: '14px',
                    boxShadow: 'none'
                }}
            >
                <BannerContent>
                    <Web24 color={colors.black} />
                    <BannerText>
                        <HBText type="title18">{t('banner_title')}</HBText>
                        <HBText type="body14" color="neutral900">
                            {t('banner_description')}
                            <HBLink
                                isNewTab
                                testIdName="sharing-learn-more"
                                textType="body14"
                                href="https://help.honeybook.com/en/articles/9162513-sharing-your-templates-publicly"
                            >
                                {t('learn_more')}
                            </HBLink>
                        </HBText>
                    </BannerText>
                </BannerContent>
            </Banner>
            <LinkContainer>
                <HBTooltip
                    disabled={!isMediumUp}
                    target={
                        <TemplateLink>
                            <Link type={fontSize} testId={templateLinkTestId}>
                                {publicConfiguration?.public_url}
                            </Link>
                        </TemplateLink>
                    }
                    maxWidth="100%"
                >
                    {publicConfiguration?.public_url}
                </HBTooltip>
                <LinkButtons>
                    {openLinkButton}
                    <LinkButton
                        onClick={() => {
                            copyLink(publicConfiguration.public_url);
                        }}
                        size="sm"
                        customTheme={{
                            fontSize: fontSize
                        }}
                        testIdName="copy-link"
                    >
                        {copyButtonIcon}
                        {t('copy_cta')}
                    </LinkButton>
                </LinkButtons>
            </LinkContainer>
            <HBButton
                id="deactivate_cta"
                onClick={() => {
                    trackEvent({
                        event: 'deactivate share link modal',
                        action: 'load'
                    });
                    setIsDeactivateModalOpen(true);
                }}
                buttonTheme="secondary-solid-primary"
                loading={isLoading}
                fullWidth={!isMediumUp}
            >
                {t('deactivate_cta')}
            </HBButton>
            <HBModal
                isOpen={isDeactivateModalOpen}
                onClose={() => {
                    setIsDeactivateModalOpen(false);
                }}
                hideCloseButton
            >
                <DeactivateModalContent>
                    <TriangleExclamationMark42 color={colors.neutral900} />
                    <DeactivateModalText>
                        <HBText type="title18" align="center">
                            {t('deactivate_modal.title')}
                        </HBText>
                        <HBText type="body14" align="center">
                            {t('deactivate_modal.description')}
                        </HBText>
                    </DeactivateModalText>
                    <DeactivateModalActions>
                        <HBButton
                            id="cancel_cta"
                            onClick={() => {
                                setIsDeactivateModalOpen(false);
                            }}
                            buttonTheme="secondary-invisible-primary"
                        >
                            {t('deactivate_modal.cancel_cta')}
                        </HBButton>
                        <HBButton
                            id="confirm_cta"
                            onClick={deactivateLink}
                            buttonTheme="secondary-solid-destructive"
                        >
                            {t('deactivate_modal.confirm_cta')}
                        </HBButton>
                    </DeactivateModalActions>
                </DeactivateModalContent>
            </HBModal>
        </ModalContent>
    );
};

export default observer(PublishedTemplateModalContent);

const Banner = styled(HBBanner)<{ noTabs: boolean }>`
    margin: 32px 0 0;
    ${is('noTabs')`
        margin: 5px 0px 0px;
    `}
`;

const BannerContent = styled.div`
    display: flex;
    flex-direction: row;
    align-items: self-start;
    gap: 8px;
`;

const BannerText = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

export const ModalContent = styled.div<{ noTabs?: boolean }>`
    display: flex;
    flex-direction: column;
    gap: 32px;
    align-items: flex-start;
    max-width: 464px;
    padding: 0 32px 32px;
    ${is('noTabs')`
        gap: 25px;
    `}
`;

export const TemplateLink = styled.div`
    background-color: ${colors.neutral100};
    border-radius: 4px;
    align-content: center;
    text-overflow: ellipsis;
    white-space: nowrap;
    user-select: none;
    overflow: hidden;
    height: 36px;
    ${mediumUp} {
        max-width: 254px;
    }
`;

export const LinkContainer = styled.div`
    display: flex;
    flex-direction: row;
    gap: 8px;
    width: 100%;
    ${upToMedium} {
        flex-direction: column;
    }
`;

export const OptionsContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

export const Link = styled(HBText)`
    padding: 0 8px;
`;

const DeactivateModalContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    width: 284px;
    align-items: center;
`;

const DeactivateModalText = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    align-items: center;
`;

const DeactivateModalActions = styled.div`
    display: flex;
    gap: 12px;
`;

export const LinkButtons = styled.div`
    display: flex;
    flex-direction: row;
    gap: 8px;
`;

export const LinkButton = styled(HBButton).attrs({
    textTransform: 'uppercase'
})`
    display: flex;
    gap: 8px;
    ${upToMedium} {
        flex-grow: 1;
    }
`;
